import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BookmarkPlus,
  History,
  ListChecks,
  MapPin,
  Plus,
  Trash2,
} from 'lucide-react';
import { LIST_UNIT_LABELS, ListUnit, PaymentMethod } from '@kick/types';
import { createListOrderSchema } from '@kick/validation';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { useAuth } from '@/lib/auth-store';
import { useUbicacion } from '@/lib/ubicacion';
import {
  useCrearPedidoLista,
  useDirecciones,
  useEliminarListaGuardada,
  useGuardarLista,
  useListasGuardadas,
  useSugerenciasLista,
  useUltimaLista,
  type DetalleComercio,
} from '@/lib/queries';
import { Boton, Campo, Entrada } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Renglon {
  key: number;
  name: string;
  quantity: number;
  unit: ListUnit;
  note: string;
  allowSubstitute: boolean;
}

let contador = 1;
const renglonVacio = (): Renglon => ({
  key: contador++,
  name: '',
  quantity: 1,
  unit: ListUnit.UNIT,
  note: '',
  allowSubstitute: true,
});

/**
 * Pedido por lista: la pantalla del cliente en un supermercado o droguería.
 *
 * No hay catálogo que mirar. El cliente escribe su lista —el autocompletar
 * sugiere lo que este comercio ya cotizó alguna vez, con su último precio—,
 * fija si quiere un tope de gasto, y la manda. El comercio la cotiza renglón
 * por renglón y el precio real se confirma después, en la pantalla del
 * pedido, en vivo.
 */
export function PedidoPorLista({ comercio }: { comercio: DetalleComercio }) {
  const navigate = useNavigate();
  const avisos = useAvisos();
  const conSesion = !!useAuth((e) => e.accessToken);
  const ubicacion = useUbicacion((s) => s.ubicacion);
  const direcciones = useDirecciones(conSesion);

  /**
   * A dónde se entrega: la ubicación elegida en la tienda o, si no hay, la
   * dirección predeterminada del cliente. Que tener direcciones guardadas
   * baste: no se le manda a elegir lo que ya eligió una vez.
   */
  const predeterminada =
    direcciones.data?.find((d) => d.isDefault) ?? direcciones.data?.[0];
  const direccionElegida = ubicacion?.addressId
    ? { id: ubicacion.addressId, addressLine: ubicacion.addressLine }
    : predeterminada
      ? { id: predeterminada.id, addressLine: predeterminada.addressLine }
      : undefined;
  const crear = useCrearPedidoLista();
  const guardarLista = useGuardarLista();
  const eliminarLista = useEliminarListaGuardada();
  const guardadas = useListasGuardadas(conSesion ? comercio.id : null);
  const ultima = useUltimaLista(conSesion ? comercio.id : null);

  const [renglones, setRenglones] = useState<Renglon[]>([renglonVacio()]);
  const [tope, setTope] = useState('');
  const [pago, setPago] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [notas, setNotas] = useState('');

  const cambiar = (key: number, cambios: Partial<Renglon>): void =>
    setRenglones((r) => r.map((x) => (x.key === key ? { ...x, ...cambios } : x)));

  const cargar = (items: { name: string; quantity: number; unit: string; note?: string | null; allowSubstitute?: boolean }[]): void => {
    setRenglones(
      items.map((i) => ({
        key: contador++,
        name: i.name,
        quantity: i.quantity,
        unit: (i.unit as ListUnit) ?? ListUnit.UNIT,
        note: i.note ?? '',
        allowSubstitute: i.allowSubstitute ?? true,
      })),
    );
  };

  const armar = () => ({
    merchantId: comercio.id,
    addressId: direccionElegida?.id ?? '',
    items: renglones
      .filter((r) => r.name.trim().length >= 2)
      .map((r) => ({
        name: r.name.trim(),
        quantity: r.quantity,
        unit: r.unit,
        ...(r.note.trim() ? { note: r.note.trim() } : {}),
        allowSubstitute: r.allowSubstitute,
      })),
    paymentMethod: pago,
    ...(Number(tope) > 0 ? { budgetCap: Number(tope) * 100 } : {}),
    ...(notas.trim() ? { customerNotes: notas.trim() } : {}),
  });

  const enviar = (): void => {
    const datos = armar();
    if (!datos.addressId) {
      avisos.error('Elige primero tu dirección de entrega');
      navigate('/mis-direcciones');
      return;
    }
    const revision = createListOrderSchema.safeParse(datos);
    if (!revision.success) {
      avisos.error(revision.error.issues[0]?.message ?? 'Revisa tu lista');
      return;
    }
    crear.mutate(datos, {
      onSuccess: (pedido) => {
        avisos.exito(`${pedido.code} enviado. El comercio está cotizando tu lista.`);
        navigate(`/pedidos/${pedido.id}`);
      },
      onError: (e) => avisos.error(getErrorMessage(e)),
    });
  };

  const guardar = (): void => {
    const items = armar().items;
    if (items.length === 0) {
      avisos.error('Agrega productos antes de guardar');
      return;
    }
    const nombre = window.prompt('Ponle nombre a esta lista (ej. Mercado quincenal):');
    if (!nombre?.trim()) return;
    guardarLista.mutate(
      { merchantId: comercio.id, name: nombre.trim(), items },
      {
        onSuccess: () => avisos.exito(`Lista «${nombre.trim()}» guardada`),
        onError: (e) => avisos.error(getErrorMessage(e)),
      },
    );
  };

  const validos = renglones.filter((r) => r.name.trim().length >= 2).length;

  return (
    <div className="mx-auto max-w-3xl pb-24 sm:pb-10">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-kick-gray transition hover:text-kick-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Comercios
      </Link>

      <div className="flex items-center gap-3">
        {comercio.logoUrl && (
          <img src={comercio.logoUrl} alt="" className="h-14 w-14 rounded-kick object-cover" />
        )}
        <div>
          <h1 className="text-2xl font-bold text-kick-dark">{comercio.name}</h1>
          <p className="flex items-center gap-1.5 text-sm text-kick-gray">
            <ListChecks className="h-4 w-4 text-kick-red" />
            Pedido por lista: escribe lo que necesitas y el comercio te confirma precios y
            disponibilidad en minutos.
          </p>
        </div>
      </div>

      {conSesion && ((ultima.data?.length ?? 0) > 0 || (guardadas.data?.length ?? 0) > 0) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {(ultima.data?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => cargar(ultima.data!)}
              className="inline-flex items-center gap-1.5 rounded-full border border-kick-gray/25 bg-white px-3 py-1.5 text-sm font-semibold text-kick-ink hover:border-kick-dark"
            >
              <History className="h-4 w-4" />
              Usar mi última lista ({ultima.data!.length})
            </button>
          )}
          {(guardadas.data ?? []).map((l) => (
            <span
              key={l.id}
              className="inline-flex items-center gap-1 rounded-full border border-kick-gray/25 bg-white pl-3 pr-1 py-1 text-sm"
            >
              <button
                type="button"
                onClick={() => cargar(l.items)}
                className="font-semibold text-kick-ink hover:text-kick-red"
              >
                {l.name}
              </button>
              <button
                type="button"
                aria-label={`Eliminar la lista ${l.name}`}
                onClick={() => eliminarLista.mutate(l.id)}
                className="rounded-full p-1 text-kick-gray hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {renglones.map((r, i) => (
          <RenglonEditor
            key={r.key}
            numero={i + 1}
            renglon={r}
            slug={comercio.slug}
            onCambio={(c) => cambiar(r.key, c)}
            onQuitar={
              renglones.length > 1
                ? () => setRenglones((lista) => lista.filter((x) => x.key !== r.key))
                : undefined
            }
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Boton
          variante="secundario"
          icono={<Plus className="h-4 w-4" />}
          onClick={() => setRenglones((r) => [...r, renglonVacio()])}
          disabled={renglones.length >= 80}
        >
          Agregar producto
        </Boton>
        {conSesion && (
          <Boton
            variante="secundario"
            icono={<BookmarkPlus className="h-4 w-4" />}
            onClick={guardar}
          >
            Guardar esta lista
          </Boton>
        )}
      </div>

      <section className="card-kick mt-6 space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Tope de gasto (pesos, opcional)"
            ayuda="El comercio lo ve mientras cotiza y se ajusta a tu presupuesto."
          >
            <Entrada
              value={tope}
              onChange={(e) => setTope(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              placeholder="Ej: 150000"
            />
          </Campo>
          <Campo etiqueta="¿Cómo vas a pagar?" ayuda="Pagas al recibir, cuando el total ya esté confirmado.">
            <div className="flex gap-2">
              {([PaymentMethod.CASH, PaymentMethod.TRANSFER] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPago(m)}
                  className={cn(
                    'flex-1 rounded-kick border px-3 py-2.5 text-sm font-semibold',
                    pago === m
                      ? 'border-kick-red bg-red-50 text-kick-red'
                      : 'border-kick-gray/25 text-kick-gray',
                  )}
                >
                  {m === PaymentMethod.CASH ? 'Efectivo' : 'Transferencia'}
                </button>
              ))}
            </div>
          </Campo>
        </div>

        <Campo etiqueta="Nota para el comercio" ayuda="Opcional.">
          <Entrada
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            maxLength={300}
            placeholder="Ej: Si algo no hay, llámame"
          />
        </Campo>

        <div className="flex items-start gap-2 rounded-kick bg-kick-light p-3 text-sm text-kick-gray">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-kick-red" />
          <p>
            {direccionElegida ? (
              <>
                Entrega en <strong className="text-kick-ink">{direccionElegida.addressLine}</strong>{' '}
                <Link to="/mis-direcciones" className="text-kick-red hover:underline">
                  cambiar
                </Link>
                . El domicilio se cobra por distancia; los productos te los confirma el comercio
                antes de preparar nada.
              </>
            ) : (
              <>
                <Link to="/mis-direcciones" className="text-kick-red hover:underline">
                  Elige tu dirección de entrega
                </Link>{' '}
                para poder pedir.
              </>
            )}
          </p>
        </div>

        {conSesion ? (
          <Boton
            className="w-full py-3 text-base"
            cargando={crear.isPending}
            disabled={validos === 0}
            onClick={enviar}
          >
            Enviar mi lista ({validos} producto{validos === 1 ? '' : 's'})
          </Boton>
        ) : (
          <Link to="/acceso" className="block">
            <Boton className="w-full py-3 text-base">
              Entra para enviar tu lista
            </Boton>
          </Link>
        )}
      </section>
    </div>
  );
}

function RenglonEditor({
  numero,
  renglon,
  slug,
  onCambio,
  onQuitar,
}: {
  numero: number;
  renglon: Renglon;
  slug: string;
  onCambio: (c: Partial<Renglon>) => void;
  onQuitar?: () => void;
}) {
  const [enfocado, setEnfocado] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const reloj = useRef<ReturnType<typeof setTimeout>>(undefined);
  const sugerencias = useSugerenciasLista(slug, busqueda);

  // El autocompletar espera medio segundo de silencio: no una consulta por tecla.
  useEffect(() => {
    clearTimeout(reloj.current);
    const texto = renglon.name.trim();
    if (texto.length < 2) {
      setBusqueda('');
      return;
    }
    reloj.current = setTimeout(() => setBusqueda(texto), 400);
    return () => clearTimeout(reloj.current);
  }, [renglon.name]);

  const opciones = enfocado ? (sugerencias.data ?? []) : [];

  return (
    <div className="card-kick p-4">
      <div className="flex items-start gap-3">
        <span className="mt-2.5 w-5 shrink-0 text-sm font-bold text-kick-gray">{numero}.</span>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="relative">
            <Entrada
              value={renglon.name}
              onChange={(e) => onCambio({ name: e.target.value })}
              onFocus={() => setEnfocado(true)}
              onBlur={() => setTimeout(() => setEnfocado(false), 150)}
              maxLength={200}
              placeholder="Ej: Arroz Diana 500 g"
              aria-label={`Producto ${numero}`}
            />
            {opciones.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-kick border border-kick-gray/20 bg-white shadow-lg">
                {opciones.map((s) => (
                  <li key={`${s.name}-${s.unit}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onCambio({ name: s.name, unit: (s.unit as ListUnit) ?? ListUnit.UNIT });
                        setEnfocado(false);
                      }}
                      className="flex w-full items-baseline justify-between px-3 py-2 text-left text-sm hover:bg-kick-light"
                    >
                      <span className="text-kick-ink">{s.name}</span>
                      <span className="shrink-0 text-xs text-kick-gray">
                        la vez pasada {formatCOP(s.lastPrice)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center rounded-kick border border-kick-gray/25">
              <button
                type="button"
                aria-label="Menos"
                onClick={() => onCambio({ quantity: Math.max(1, renglon.quantity - 1) })}
                className="px-3 py-1.5 font-bold text-kick-gray hover:text-kick-red"
              >
                −
              </button>
              <span className="w-8 text-center text-sm font-semibold text-kick-ink">
                {renglon.quantity}
              </span>
              <button
                type="button"
                aria-label="Más"
                onClick={() => onCambio({ quantity: Math.min(999, renglon.quantity + 1) })}
                className="px-3 py-1.5 font-bold text-kick-gray hover:text-kick-red"
              >
                +
              </button>
            </div>

            <select
              value={renglon.unit}
              onChange={(e) => onCambio({ unit: e.target.value as ListUnit })}
              aria-label={`Unidad del producto ${numero}`}
              className="rounded-kick border border-kick-gray/25 bg-white px-2 py-2 text-sm text-kick-ink"
            >
              {Object.values(ListUnit).map((u) => (
                <option key={u} value={u}>
                  {LIST_UNIT_LABELS[u]}
                </option>
              ))}
            </select>

            <Entrada
              value={renglon.note}
              onChange={(e) => onCambio({ note: e.target.value })}
              maxLength={200}
              placeholder="Nota: marca, madurez, presentación…"
              className="min-w-40 flex-1"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-kick-gray">
            <input
              type="checkbox"
              checked={renglon.allowSubstitute}
              onChange={(e) => onCambio({ allowSubstitute: e.target.checked })}
            />
            Si no hay, que me sugieran un reemplazo (si no, lo quitan)
          </label>
        </div>

        {onQuitar && (
          <button
            type="button"
            onClick={onQuitar}
            aria-label={`Quitar producto ${numero}`}
            className="mt-2 rounded-kick p-1.5 text-kick-gray hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
